import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from '@heroicons/react/20/solid';

/** Custom single-select (in-page list, not the native OS select menu). Same pattern as Meeting Preparation. */
export default function SingleSelectDropdown<T extends string | number>({
  label,
  options,
  selected,
  onChange,
  renderOption,
  enableSearch,
  placeholder,
  className = 'w-full min-w-0',
  allowClear = true,
  triggerId,
  triggerClassName,
  disabled = false,
  listBoxClassName,
}: {
  label: string;
  options: T[];
  selected: T | '';
  onChange: (newSelected: T | '') => void;
  renderOption?: (value: T) => string;
  enableSearch?: boolean;
  placeholder?: string;
  /** Outer wrapper width / flex (e.g. "w-full min-w-0 flex-1") */
  className?: string;
  /** When false, no “clear” row (use for month/year). Default true. */
  allowClear?: boolean;
  /** Associates the trigger with an external `<label htmlFor>`. */
  triggerId?: string;
  /** Overrides default trigger button styles (e.g. match native `<select>` fields). */
  triggerClassName?: string;
  disabled?: boolean;
  /** Overrides `<ul role="listbox">` text/size classes (e.g. `text-xs`). */
  listBoxClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  const selectOption = (value: T | '') => {
    onChange(value);
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const filteredOptions =
    enableSearch && search
      ? options.filter((opt) =>
          (renderOption ? renderOption(opt) : String(opt)).toLowerCase().includes(search.toLowerCase())
        )
      : options;

  const hasValue = selected !== '' && selected !== undefined && selected !== null;
  const displayText = hasValue
    ? renderOption
      ? renderOption(selected as T)
      : String(selected)
    : placeholder || `Select ${label.toLowerCase()}`;

  const showClearRow = allowClear && hasValue;

  return (
    <div className={`relative text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        id={triggerId}
        disabled={disabled}
        onClick={() => setOpen(!open)}
        className={
          triggerClassName ??
          'flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50'
        }
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={hasValue ? '' : 'text-gray-500'}>{displayText}</span>
        {open ? (
          <ChevronUpIcon className="h-4 w-4 shrink-0 text-gray-500" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
        )}
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {enableSearch && (
            <div className="border-b border-gray-100 p-2">
              <input
                type="text"
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
          <ul className={listBoxClassName ?? 'py-1 text-sm text-gray-700'} role="listbox">
            {showClearRow && (
              <>
                <li
                  role="option"
                  className="flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-gray-100"
                  onClick={() => selectOption('')}
                >
                  <span className="text-gray-500">{placeholder || `Select ${label.toLowerCase()}`}</span>
                </li>
                <hr className="border-gray-100" />
              </>
            )}
            {filteredOptions.map((opt) => (
              <li
                key={String(opt)}
                role="option"
                className={`flex cursor-pointer items-center justify-between px-4 py-2 hover:bg-gray-100 ${
                  selected === opt ? 'bg-action-surface' : ''
                }`}
                onClick={() => selectOption(opt)}
              >
                <span>{renderOption ? renderOption(opt) : String(opt)}</span>
                {selected === opt && <CheckIcon className="h-4 w-4 shrink-0 text-action" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
